import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useActiveCompanyId } from '../store/useCompanyStore';
import {
  createActor,
  createClientIndustry,
  deleteActor,
  getActor,
  linkActorClient,
  listActors,
  listClientActors,
  listClientIndustries,
  unlinkActorClient,
  updateActor,
  updateActorClient,
  type ActorFilters,
  type ActorInput,
  type LinkClientInput,
} from '../lib/actorsApi';
import { updateClient, type ClientInput } from '../lib/clientsApi';

const ACTORS = ['actors'] as const;
const INDUSTRIES = ['client-industries'] as const;

/** The client industries (the "Industrie" dropdown). */
export function useClientIndustries(enabled = true) {
  return useQuery({
    queryKey: INDUSTRIES,
    queryFn: listClientIndustries,
    enabled,
    staleTime: 60_000,
  });
}

export function useCreateClientIndustry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createClientIndustry(name),
    onSuccess: () => void qc.invalidateQueries({ queryKey: INDUSTRIES }),
  });
}

/**
 * Set a client's industry from the Actori screens (the actor's clients, the
 * link dialog), so the conflict check has what it needs without leaving the
 * page. Refreshes clients and actors, whose conflicts depend on it.
 */
export function useSetClientIndustry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, industryId }: { clientId: string; industryId: string | null }) =>
      updateClient(clientId, { industryId } as ClientInput),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ACTORS });
    },
  });
}

/** The actor list, scoped to the active firm like the client list. */
export function useActors(filters: ActorFilters = {}, enabled = true) {
  const companyId = useActiveCompanyId();
  const merged = { companyId, ...filters };
  return useQuery({
    queryKey: [...ACTORS, 'list', merged],
    queryFn: () => listActors(merged),
    enabled,
  });
}

export function useActor(id: string | undefined) {
  return useQuery({
    queryKey: [...ACTORS, 'detail', id],
    queryFn: () => getActor(id!),
    enabled: Boolean(id),
  });
}

export function useClientActors(clientId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...ACTORS, 'client', clientId],
    queryFn: () => listClientActors(clientId!),
    enabled: Boolean(clientId) && enabled,
  });
}

function useInvalidateActors() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ACTORS });
}

export function useCreateActor() {
  const invalidate = useInvalidateActors();
  return useMutation({
    mutationFn: (input: ActorInput) => createActor(input),
    onSettled: () => void invalidate(),
  });
}

export function useUpdateActor(id: string) {
  const invalidate = useInvalidateActors();
  return useMutation({
    mutationFn: (input: Partial<ActorInput>) => updateActor(id, input),
    onSettled: () => void invalidate(),
  });
}

export function useDeleteActor() {
  const invalidate = useInvalidateActors();
  return useMutation({
    mutationFn: (id: string) => deleteActor(id),
    onSettled: () => void invalidate(),
  });
}

export function useLinkActorClient() {
  const invalidate = useInvalidateActors();
  return useMutation({
    mutationFn: ({ actorId, ...input }: LinkClientInput & { actorId: string }) =>
      linkActorClient(actorId, input),
    onSettled: () => void invalidate(),
  });
}

export function useUpdateActorClient() {
  const invalidate = useInvalidateActors();
  return useMutation({
    mutationFn: ({
      actorId,
      clientId,
      ...input
    }: {
      actorId: string;
      clientId: string;
      usedAt?: string;
      note?: string;
    }) => updateActorClient(actorId, clientId, input),
    onSettled: () => void invalidate(),
  });
}

export function useUnlinkActorClient() {
  const invalidate = useInvalidateActors();
  return useMutation({
    mutationFn: ({ actorId, clientId }: { actorId: string; clientId: string }) =>
      unlinkActorClient(actorId, clientId),
    onSettled: () => void invalidate(),
  });
}
